/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { getDataFromPath, setDataFromPath, transformTargetModel } from '../lib/dynamicContentAssemblyUtils';
import { targetPayloadRequest } from '../lib/dynamicContentAssemblyUtils/targetSDKPayload';
import { dcaDynamicContent } from '@marriott/mi-merch-graphql';
import { useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import createMappingModelJSON from '../lib/dynamicContentAssemblyUtils/responseMapping';
import operationSignatures from '../lib/dynamicContentAssemblyUtils/_constants/operationSignatures.json';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import isEmpty from 'lodash/isEmpty';
import { IPageModel, IReturnTypeOFGetMboxParam } from '../types';
import { logger } from '@marriott/shared/mi-helper-utils';
import { useSession } from '../lib/session';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const transformJSON = require('json-map-transform');

declare global {
  interface Window {
    sessionData: any;
    mvpOffers?: {
      specialMessage?: string;
      rpcCode?: string;
      offerCode?: string;
      source?: string;
    };
    preQualShowOfferEvent: () => void;
    _satellite: Record<string, any>;
  }
}
// @todo - need to fill this out and have clients of this pass in a type for T
export type DCAModelData = {
  isTargetContentLoaded?: boolean;
  isTargetContentError?: boolean;
  metaData: Record<string, string>;
};

export const useDCADynamicModel = (mboxParameters: IReturnTypeOFGetMboxParam[], pageModel: IPageModel) => {
  if (mboxParameters?.length == 0) {
    return null;
  }

  const { log } = logger({})('useDCADynamicModel');
  const [isPrequal, setIsPrequal] = useState<boolean>(false);
  const { datalayer, mvpOffers } = useSession();
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  const [isUXlCallTriggered, setIsUXLCallTriggered] = useState(false);

  /**
   *
   * @param listOfMboxResponses refers to mboxes array of uxl call response
   * @description this function will store the updated pageModel in DCAPageModelStore
   * case-1: when uxl call is successfull pass that response to this function, then it will update pageModel according to that response..
   * case-2: if call fails, then simply call this function
   * then it will update pageModel with isTargetContentError flag for the appropriate mbox components
   *
   */
  const updatePageModel = (listOfMboxResponses: Record<string, unknown>[] = []) => {
    const newPageModel = { ...pageModel };
    mboxParameters?.forEach(parameter => {
      const mboxName = parameter?.['mboxName'];
      const mboxResponse = listOfMboxResponses?.find(item => item?.['mboxName'] == mboxName);
      const requiredPath = parameter?.path; // returns path of component eg: cqItems.merchHeroBanner
      const compModel = getDataFromPath(pageModel, requiredPath); // returns component data from pageModel
      if (mboxResponse) {
        const mappingModelJSON = createMappingModelJSON();
        const mappedResponse = transformJSON(mboxResponse, mappingModelJSON);
        const transformerData = transformTargetModel(compModel, mappedResponse);
        parameter.dcaResponse = { ...transformerData, isTargetContentLoaded: true, isTargetContentError: false };
        setDataFromPath(newPageModel, requiredPath, { ...transformerData, isTargetContentLoaded: true });
      } else {
        parameter.dcaResponse = {
          ...compModel,
          isTargetContentLoaded: true,
          isTargetContentError: true,
        };
        setDataFromPath(newPageModel, requiredPath, {
          ...compModel,
          isTargetContentLoaded: true,
          isTargetContentError: true,
        });
      }
    });
    setPageModel({ ...newPageModel, mboxData: [...mboxParameters] });
  };

  useEffect(() => {
    if (isEmpty(datalayer)) {
      log.info('datalayer is empty');
      return;
    }
    if (!AuthoringUtils.isInEditor() && AuthoringUtils.isPreviewMode()) {
      log.info('AuthoringUtils.isInEditor() && AuthoringUtils.isPreviewMode()');
      return;
    }
    const fetchAndUpdateTargetData = async () => {
      setIsUXLCallTriggered(true);
      const options = targetPayloadRequest(
        {
          locale:
            window?.sessionData?.locale?.replace('-', '_') ||
            window?._satellite?.['getVar']('brwsrLang') ||
            datalayer?.['brwsrLang'] ||
            'en_US',
          mboxParameters: mboxParameters,
          propertyToken:
            process.env['TARGET_PROPERTY_TOKEN'] ||
            useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'],
          isACDL: useClientEnvVarsStore.getState().envVarsObject?.['NEXT_PUBLIC_ENABLE_WEBSDK'],
        },
        datalayer,
        mvpOffers
      );
      if (!AuthoringUtils.isInEditor() && !AuthoringUtils.isPreviewMode()) {
        try {
          const MiApolloClient = useNextMiApolloClient(
            operationSignatures,
            useClientEnvVarsStore.getState().envVarsObject['DEPLOYED_ENV_TYPE']
          );
          const { data, errors } = await MiApolloClient.query({
            query: dcaDynamicContent,
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            variables: {
              input: options,
            },
            context: {
              headers: {},
            },
          });
          const resp = (data?.dynamicContentByChannelAndMboxes?.mboxes || []) as Record<
            string,
            Record<string, unknown> | string
          >[];
          if (errors && errors.length && resp?.length == 0) {
            throw new Error('DCA call error or mbox parameter is empty');
          }
          updatePageModel(resp);
          if (resp && resp?.length > 0) {
            resp?.forEach(item => {
              if (String((item?.['metadata'] as Record<string, unknown>)?.['prequal']) === 'true') {
                setIsPrequal(true);
              }
            });
          }
        } catch (error) {
          console.log(error);
          updatePageModel();
        }
      }
    };
    if (!isEmpty(datalayer) && !isUXlCallTriggered) {
      fetchAndUpdateTargetData();
    }
  }, [datalayer, mvpOffers, isUXlCallTriggered]);

  useEffect(() => {
    // fail safe condition if the preQualShowOfferEvent is not available on window
    if (typeof window?.preQualShowOfferEvent === 'function' && isPrequal) {
      window?.preQualShowOfferEvent();
    }
  }, [isPrequal]);

  return null;
};
